import isEqual from "fast-deep-equal/es6";
import { create } from "zustand";
import { GPS } from "../../util/geo";
import { config } from "./config";
import { Cube } from "./types";
import { FetchState, ignore } from "./util";

interface ProjectCubeState {
  state?: FetchState;
  error?: any;
  projectId?: number;
  cube?: Cube;
  selectProject: (id: number, force?: boolean) => void;
  clear: () => void;
}

export const useProjectCube = create<ProjectCubeState>((set, get) => ({
  selectProject: async (id: number, force?: boolean) => {
    const cur = get();
    if (!force && cur.projectId === id && cur.state === "fetched") return;
    set({ projectId: id, state: "fetching", error: undefined });
    const response: Response = await fetch(`/api/projects/${id}/cube`).catch(ignore);
    if (!response.ok) {
      set({ projectId: id, state: "error", error: response.status });
      return;
    }
    const data = await response.json();
    if (get().projectId === id) set({ state: "fetched", cube: data });
  },
  clear: () => set({ state: undefined, projectId: undefined, cube: undefined, error: undefined }),
}));

interface SelectionState {
  state?: FetchState;
  error?: any;
  gps?: GPS;
  cube?: Cube;
  selectGPS: (gps: GPS) => void;
  clear: () => void;
}

interface HeightState {
  state?: FetchState;
  error?: any;
  gps?: GPS;
  data?: {
    url: string;
    extent: number[];
    stats: {
      min: number;
      max: number;
      mean: number;
      histogram: [number[], number[]];
    };
    versions: any[];
    version?: number;
  };
  selectHeight: (gps: GPS, version?: number) => void;
  clear: () => void;
}

async function fetchState(url, gps, get, set, datakey) {
  const cur = get();
  // Cache hit: skip only when we already have a successful result for this gps.
  // If the previous attempt errored (or never ran), proceed so a re-select retries.
  if (cur.state === "fetched" && isEqual(gps, cur.gps)) return;
  set({ gps, state: "fetching", error: undefined, [datakey]: undefined });
  const response: Response = await fetch(url).catch(ignore);
  if (!response.ok) return set({ gps, state: "error", error: response.status });
  const data = await response.json();
  if (get().gps === gps) {
    // not changed in the meantime, update state
    set({ state: "fetched", [datakey]: data });
  }
}

export const useSelection = create<SelectionState>((set, get) => ({
  selectGPS: async (gps: GPS) => {
    await fetchState(
      `${config.referenceUrl}/api/cube?c=${gps[0]},${gps[1]}`,
      gps,
      get,
      set,
      "cube"
    );
  },
  clear: async () => set({ state: undefined, cube: undefined }),
}));

export const useHeightData = create<HeightState>((set, get) => ({
  selectHeight: async (gps: GPS, version?: number) => {
    const p = version !== undefined ? `&version=${version}` : "";
    await fetchState(
      `${config.referenceUrl}/api/image/height?c=${gps[0]},${gps[1]}${p}`,
      gps,
      get,
      set,
      "data"
    );
  },
  clear: async () => set({ state: undefined, gps: undefined, data: undefined, error: undefined }),
}));
